<script>
import AppButton from '@/components/ui/AppButton.vue'
import FormInput from '@/components/form/FormInput.vue'
import { getCategoryIcon, getSubCategoryIcon } from '@/utils/productIcons'

const norm = (name) => (name || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase()
const isOutros = (name) => ['outros', 'otros'].includes(norm(name))

export default {
	name: 'ProductRegisterFlow',
	components: { AppButton, FormInput },
	emits: ['load-sub-categories', 'save', 'dirty-change'],
	props: {
		categories: { type: Array, default: () => [] },
		subCategories: { type: Array, default: () => [] },
		isSaving: { type: Boolean, default: false },
		showFooter: { type: Boolean, default: true }
	},
	data() {
		return {
			selectedCategory: '',
			selectedSubCategory: '',
			productModel: '',
			submitted: false
		}
	},
	computed: {
		selectedCategoryData() {
			return this.categories.find((category) => category.id === this.selectedCategory) || null
		},
		selectedSubCategoryData() {
			return this.subCategories.find((subCategory) => subCategory.id === this.selectedSubCategory) || null
		},
		visibleCategories() {
			const hidden = ['cozinha', 'informatica', 'electrodomesticos']
			return this.categories
				.filter((category) => !hidden.includes(norm(category.name)))
				.sort((a, b) => Number(isOutros(a.name)) - Number(isOutros(b.name)))
		},
		sortedSubCategories() {
			return [...this.subCategories].sort((a, b) => Number(isOutros(a.name)) - Number(isOutros(b.name)))
		},
		summaryText() {
			const parts = [this.selectedCategoryData?.name, this.selectedSubCategoryData?.name, this.productModel.trim()].filter(Boolean)
			return parts.length ? parts.join('  ›  ') : this.$t('myArea.productsSection.summaryEmpty')
		},
		isFormValid() {
			return !!this.selectedCategory && !!this.selectedSubCategory && !!this.productModel.trim()
		},
		isDirty() {
			return !!this.selectedCategory || !!this.selectedSubCategory || !!this.productModel.trim()
		}
	},
	watch: {
		isDirty: {
			immediate: true,
			handler(value) { this.$emit('dirty-change', value) }
		}
	},
	methods: {
		selectCategory(categoryId) {
			if (this.selectedCategory === categoryId) return
			this.selectedCategory = categoryId
			this.selectedSubCategory = ''
			this.$emit('load-sub-categories', categoryId)
		},
		selectSubCategory(subCategoryId) {
			this.selectedSubCategory = subCategoryId
		},
		submit() {
			this.submitted = true
			if (!this.isFormValid || this.isSaving) return
			this.$emit('save', {
				categoryId: this.selectedCategory,
				subCategoryId: this.selectedSubCategory,
				name: this.productModel.trim()
			})
		},
		reset() {
			this.selectedCategory = ''
			this.selectedSubCategory = ''
			this.productModel = ''
			this.submitted = false
		},
		categoryIcon(name) { return getCategoryIcon(name) },
		subCategoryIcon(categoryName, subName) { return getSubCategoryIcon(categoryName, subName) }
	}
}
</script>

<template>
	<form class="product-flow" data-qa="product-register-form" @submit.prevent="submit">
		<div class="product-flow__step">
			<div class="product-flow__step-head">
				<span class="product-flow__step-number" aria-hidden="true">1</span>
				<span class="product-flow__step-title">{{ $t('myArea.productsSection.step1Title') }}</span>
			</div>
			<p class="product-flow__step-hint">{{ $t('myArea.productsSection.step1Hint') }}</p>

			<div class="product-flow__options" data-qa="product-category-options" role="radiogroup" :aria-label="$t('myArea.productsSection.step1Title')">
				<label
					v-for="category in visibleCategories"
					:key="category.id"
					class="product-flow__option"
					:class="[{ 'is-selected': selectedCategory === category.id }, { 'is-text-only': !categoryIcon(category.name) }]"
				>
					<input
						type="radio"
						name="product-category"
						class="product-flow__option-radio"
						data-qa="product-category-option-radio"
						:data-qa-id="category.id"
						:value="category.id"
						:checked="selectedCategory === category.id"
						@change="selectCategory(category.id)"
					>
					<img v-if="categoryIcon(category.name)" class="product-flow__option-icon" :src="categoryIcon(category.name)" alt="" aria-hidden="true">
					<span class="product-flow__option-label">{{ category.name }}</span>
				</label>
			</div>
		</div>

		<div v-show="selectedCategory" class="product-flow__step">
			<div class="product-flow__step-head">
				<span class="product-flow__step-number" aria-hidden="true">2</span>
				<span class="product-flow__step-title">{{ $t('myArea.productsSection.step2Title') }}</span>
			</div>
			<p class="product-flow__step-hint">{{ $t('myArea.productsSection.step2Hint') }}</p>

			<div class="product-flow__options" data-qa="product-subcategory-options" role="radiogroup" :aria-label="$t('myArea.productsSection.step2Title')">
				<label
					v-for="subCategory in sortedSubCategories"
					:key="subCategory.id"
					class="product-flow__option"
					:class="[{ 'is-selected': selectedSubCategory === subCategory.id }, { 'is-text-only': !subCategoryIcon(selectedCategoryData && selectedCategoryData.name, subCategory.name) }]"
				>
					<input
						type="radio"
						name="product-subcategory"
						class="product-flow__option-radio"
						data-qa="product-subcategory-option-radio"
						:data-qa-id="subCategory.id"
						:value="subCategory.id"
						:checked="selectedSubCategory === subCategory.id"
						@change="selectSubCategory(subCategory.id)"
					>
					<img v-if="subCategoryIcon(selectedCategoryData && selectedCategoryData.name, subCategory.name)" class="product-flow__option-icon" :src="subCategoryIcon(selectedCategoryData && selectedCategoryData.name, subCategory.name)" alt="" aria-hidden="true">
					<span class="product-flow__option-label">{{ subCategory.name }}</span>
				</label>
			</div>
		</div>

		<div v-show="selectedSubCategory" class="product-flow__step">
			<div class="product-flow__step-head">
				<span class="product-flow__step-number" aria-hidden="true">3</span>
				<span class="product-flow__step-title">{{ $t('myArea.productsSection.step3Title') }}</span>
			</div>
			<p class="product-flow__step-hint">{{ $t('myArea.productsSection.step3Hint') }}</p>

			<FormInput
				data-qa="product-model-input"
				v-model="productModel"
				:label="$t('form.labels.model')"
				:placeholder="$t('form.placeholder.model')"
				:is-required="true"
				:is-error="submitted && !productModel.trim()"
				:message-error="$t('form.messageError.correctlyFull')"
				input-id="product-model"
			/>
		</div>

		<div v-if="showFooter" class="product-flow__footer">
			<span class="product-flow__summary" data-qa="product-summary-text">{{ summaryText }}</span>
			<AppButton data-qa="product-save-button" type="submit" variant="primary" :disabled="isSaving || !isFormValid" :aria-busy="isSaving">
				{{ $t(isSaving ? 'buttons.saving' : 'buttons.saveProduct') }}
			</AppButton>
		</div>
	</form>
</template>

<style lang="sass" scoped>
.product-flow
	display: flex
	flex-direction: column
	gap: 2rem
	width: 100%

	&__step
		display: flex
		flex-direction: column
		gap: 0.5rem

		& + &
			padding-top: 1.75rem
			border-top: 1px solid var(--lg-light-gray-300)

	&__step-head
		display: flex
		align-items: center
		gap: 0.75rem

	&__step-number
		display: inline-flex
		flex: 0 0 auto
		align-items: center
		justify-content: center
		width: 1.625rem
		height: 1.625rem
		border-radius: 50%
		font-family: var(--lgei-headline)
		font-size: 0.8125rem
		background-color: var(--lg-dark-gray-200)
		color: var(--white)

	&__step-title
		font-family: var(--lgei-headline)
		font-size: 1.125rem
		color: var(--lg-dark-gray-200)

	&__step-hint
		margin: 0 0 0.5rem 2.375rem
		font-family: var(--lgei-text)
		font-size: 0.8125rem
		color: var(--lg-mid-gray-200)

	&__options
		display: grid
		grid-template-columns: repeat(auto-fill, minmax(9rem, 1fr))
		gap: 0.875rem
		margin-left: 2.375rem

		@media (max-width: 30rem)
			margin-left: 0
			grid-template-columns: repeat(auto-fill, minmax(7.5rem, 1fr))

	&__option
		position: relative
		display: flex
		flex-direction: column
		align-items: center
		justify-content: flex-start
		gap: 0.75rem
		padding: 1.375rem 0.875rem
		border: 1px solid var(--lg-light-gray-300)
		border-radius: 0.875rem
		background-color: var(--lg-light-gray-100)
		cursor: pointer
		transition: border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease

		&:hover
			border-color: var(--lg-red-active)

		&.is-selected
			border: 2px solid var(--lg-red-active)
			background-color: rgba(var(--lg-red-active-rgb), 0.06)

		&.is-text-only
			justify-content: center
			min-height: 4.75rem

	&__option-radio
		position: absolute
		width: 1px
		height: 1px
		margin: -1px
		padding: 0
		overflow: hidden
		clip: rect(0, 0, 0, 0)
		white-space: nowrap
		border: 0

		&:focus-visible ~ .product-flow__option-icon,
		&:focus-visible ~ .product-flow__option-label
			outline: 2px solid var(--lg-red-active)
			outline-offset: 2px

	&__option-icon
		display: block
		width: 3.25rem
		height: 3.25rem
		object-fit: contain
		transform: translateY(0) scale(1)
		transition: transform 0.4s cubic-bezier(.28,0,.22,.98)

		

	&__option:hover &__option-icon,
	&__option.is-selected &__option-icon
		transform: translateY(0) scale(1.1)

	&__option-label
		font-family: var(--lgei-text)
		font-size: 0.875rem
		line-height: 1.3
		text-align: center
		color: var(--lg-dark-gray-200)

	&__footer
		display: flex
		flex-direction: column
		align-items: flex-start
		gap: 0.875rem
		padding-top: 1.5rem
		border-top: 1px solid var(--lg-light-gray-300)

		:deep(.AppButton__primary)
			margin: 0

	&__summary
		order: -1
		font-family: var(--lgei-text)
		font-size: 0.8125rem
		color: var(--lg-mid-gray-200)
</style>
